import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { classesAPI, departmentsAPI, rolesAPI, usersAPI } from '../services/api';
import { getRoleBadgeColor, getRoleDisplayName } from '../utils/helpers';
import { ChevronLeft, ChevronRight, Download, FileUp, Pencil, Plus, Power, RotateCcw, Search, Smartphone, Tag, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  getEmploymentTypeOptions,
  getEmploymentTypeLabel,
  getWorkArrangementLabel,
  workArrangementOptions,
} from '../utils/employment';

const emptyForm = {
  full_name: '',
  email: '',
  role: 'student',
  department_id: '',
  class_id: '',
  linked_student_ids: [],
  employee_id: '',
  phone: '',
  employment_type: 'full_time',
  work_arrangement: 'on_site',
  password: '',
  attendance_enabled: true,
  is_active: false,
};

const Users = () => {
  const { user, hasPermission, entityCategory, terminology } = useAuth();
  const employmentTypeOptions = useMemo(
    () => getEmploymentTypeOptions(user?.entity?.code),
    [user?.entity?.code]
  );
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [assigning, setAssigning] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [employmentTypeFilter, setEmploymentTypeFilter] = useState('');
  const [workArrangementFilter, setWorkArrangementFilter] = useState('');
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importingUsers, setImportingUsers] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const tableScrollRef = useRef(null);

  const loadUsers = useCallback(async () => {
    const res = await usersAPI.getAll();
    setUsers(res.data.users);
  }, []);

  useEffect(() => {
    Promise.all([loadUsers(), departmentsAPI.getAll(), classesAPI.getAll(), rolesAPI.getAll()])
      .then(([, departmentsRes, classesRes, rolesRes]) => {
        setDepartments(departmentsRes.data.departments);
        setSections(classesRes.data.classes);
        setRoles(rolesRes.data.roles);
      })
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setLoading(false));
  }, [loadUsers]);

  const roleOptions = useMemo(
    () => [{ code: 'all', name: 'All Users' }, ...roles.map((role) => ({ code: role.code, name: getRoleDisplayName(role.code, entityCategory, role.name) }))],
    [roles, entityCategory]
  );

  const normalizedUsers = useMemo(() => {
    const unique = new Map();

    for (const user of users) {
      if (!unique.has(user.id)) {
        unique.set(user.id, user);
      }
    }

    return Array.from(unique.values());
  }, [users]);

  const studentOptions = useMemo(
    () => normalizedUsers.filter((user) => user.role === 'student' && user.is_active),
    [normalizedUsers]
  );

  const handleAssignTag = async (userId) => {
    if (!tagInput.trim()) {
      toast.error('Enter a tag UID');
      return;
    }

    try {
      await usersAPI.assignTag({ user_id: userId, tag_uid: tagInput.trim() });
      toast.success('Tag assigned successfully');
      setAssigning(null);
      setTagInput('');
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to assign tag');
    }
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();

    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error('Full name and email are required');
      return;
    }

    try {
      setSavingUser(true);
      const payload = {
        ...form,
        department_id: form.department_id || null,
        class_id: form.class_id || null,
        linked_student_ids: form.role === 'parent' ? form.linked_student_ids : [],
      };
      if (editingUserId) {
        await usersAPI.update(editingUserId, payload);
        toast.success('User updated successfully');
      } else {
        await usersAPI.create(payload);
        toast.success('User created successfully');
      }
      setForm(emptyForm);
      setEditingUserId(null);
      setShowCreateForm(false);
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create user');
    } finally {
      setSavingUser(false);
    }
  };

  const startEditUser = (user) => {
    setEditingUserId(user.id);
    setForm({
      full_name: user.full_name || '',
      email: user.email || '',
      role: user.role || 'student',
      department_id: user.department_id || '',
      class_id: user.section_id || '',
      linked_student_ids: Array.isArray(user.linked_students) ? user.linked_students.map((student) => student.id) : [],
      employee_id: user.employee_id || '',
      phone: user.phone || '',
      employment_type: user.employment_type || '',
      work_arrangement: user.work_arrangement || '',
      password: '',
      attendance_enabled: Boolean(user.attendance_enabled),
      is_active: user.is_active,
    });
    setShowCreateForm(true);
  };

  const filtered = normalizedUsers.filter((user) => {
    const lifecycleStatus = user.lifecycle_status || (user.is_active ? 'active' : 'inactive');
    const matchesSearch =
      user.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      user.email?.toLowerCase().includes(search.toLowerCase()) ||
      user.employee_id?.toLowerCase().includes(search.toLowerCase());

    const matchesClassification = [
      getEmploymentTypeLabel(user.employment_type),
      getWorkArrangementLabel(user.work_arrangement),
    ].some((value) => value.toLowerCase().includes(search.toLowerCase()));

    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesEmploymentType = !employmentTypeFilter || user.employment_type === employmentTypeFilter;
    const matchesWorkArrangement = !workArrangementFilter || user.work_arrangement === workArrangementFilter;
    const matchesStatus = statusFilter === 'all'
      ? lifecycleStatus !== 'deactivated'
      : lifecycleStatus === statusFilter;

    return (matchesSearch || matchesClassification) && matchesRole && matchesEmploymentType && matchesWorkArrangement && matchesStatus;
  });

  const handleSectionChange = async (userId, classId) => {
    try {
      await usersAPI.updateSection(userId, { class_id: classId || null });
      toast.success('Section updated successfully');
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update section');
    }
  };

  const handleStatusChange = async (user, isActive) => {
    const action = isActive ? 'activate' : 'move to the inactive directory';
    const activationMessage = user.attendance_enabled
      ? 'Attendance tracking will start from this point.'
      : 'The account will become active, but attendance remains disabled.';
    if (!window.confirm(`${action.charAt(0).toUpperCase()}${action.slice(1)} ${user.full_name}? ${isActive ? activationMessage : 'Future attendance tracking and login will stop; historical records and enrollment data will remain.'}`)) {
      return;
    }

    try {
      const response = await usersAPI.setStatus(user.id, isActive);
      toast.success(response.data.message);
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update user status');
    }
  };

  const handleDeactivate = async (user) => {
    if (!window.confirm(`Deactivate ${user.full_name}? The user will be removed from operational directories and cannot log in or record attendance. Historical data will be retained.`)) return;
    try {
      const response = await usersAPI.remove(user.id);
      toast.success(response.data.message);
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to deactivate user');
    }
  };

  const handleRestore = async (user) => {
    if (!window.confirm(`Restore ${user.full_name} to the inactive directory? Attendance will remain paused until explicitly activated.`)) return;
    try {
      const response = await usersAPI.restore(user.id);
      toast.success(response.data.message);
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to restore user');
    }
  };

  const handleReplaceMobileDevice = async (user) => {
    if (!window.confirm(`Remove ${user.full_name}'s registered phone? Attendance and face history will remain. The user must sign in with their own credentials and register the replacement phone.`)) return;
    try {
      const response = await usersAPI.replaceMobileDevice(user.id);
      toast.success(response.data.message);
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove the registered phone');
    }
  };

  const canCreate = hasPermission('users.create');
  const canEdit = hasPermission('users.edit');
  const canAssignTag = hasPermission('users.assign_tag');
  const canManageMobileDevice = user?.role === 'admin';
  const currentRoleLabel = roleOptions.find((role) => role.code === roleFilter)?.name || 'Users';

  const scrollUsersTable = (direction) => {
    tableScrollRef.current?.scrollBy({
      left: direction * Math.max(420, Math.round(tableScrollRef.current.clientWidth * 0.7)),
      behavior: 'smooth',
    });
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      const response = await usersAPI.downloadImportTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `smartencampus-${entityCategory}-user-import-template.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${terminology.categoryLabel} user template downloaded`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to download user template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleImportUsers = async (event) => {
    event.preventDefault();
    if (!importFile) return toast.error('Choose a completed .xlsx user template');
    try {
      setImportingUsers(true);
      setImportErrors([]);
      const data = new FormData();
      data.append('file', importFile);
      const response = await usersAPI.importWorkbook(data);
      toast.success(response.data.message || 'Users imported successfully');
      setImportFile(null);
      event.target.reset();
      await loadUsers();
    } catch (err) {
      setImportErrors(err.response?.data?.errors || []);
      toast.error(err.response?.data?.error || 'Failed to import users');
    } finally {
      setImportingUsers(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-500 text-sm mt-1">{filtered.length} {currentRoleLabel.toLowerCase()} shown</p>
        </div>
        {canCreate && <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleDownloadTemplate}
            disabled={downloadingTemplate}
            className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-60"
          >
            <Download size={16} />
            {downloadingTemplate ? 'Downloading...' : `Download ${terminology.categoryLabel} Template`}
          </button>
          <button
            onClick={() => {
              setShowCreateForm((prev) => {
                const next = !prev;
                if (next && !editingUserId) {
                  setForm((current) => ({
                    ...emptyForm,
                    role: roleFilter !== 'all' ? roleFilter : current.role || 'student',
                  }));
                }
                return next;
              });
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            {showCreateForm ? <Plus size={16} /> : <UserPlus size={16} />}
            {showCreateForm ? 'Close Form' : `Add ${roleFilter === 'all' ? 'User' : currentRoleLabel.replace(/s$/i, '')}`}
          </button>
        </div>}
      </div>

      {canCreate && <form onSubmit={handleImportUsers} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[260px] flex-1">
            <label htmlFor="user-import-file" className="mb-1 block text-sm font-medium text-gray-700">Upload completed user template</label>
            <input id="user-import-file" type="file" accept=".xlsx" required
              onChange={(event) => { setImportFile(event.target.files?.[0] || null); setImportErrors([]); }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </div>
          <button type="submit" disabled={importingUsers || !importFile}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
            <FileUp size={16} />{importingUsers ? 'Uploading...' : 'Upload Users'}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">Upload the .xlsx file downloaded above. Invalid rows prevent the entire import.</p>
        {importErrors.length > 0 && <div className="mt-3 max-h-48 overflow-auto rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {importErrors.map((item) => <p key={item.row}>Row {item.row}: {item.errors.join(', ')}</p>)}
        </div>}
      </form>}

      <div className="flex flex-wrap gap-2">
        {[['all', 'All directory'], ['active', 'Active'], ['inactive', 'Inactive'], ['deactivated', 'Deactivated']].map(([value, label]) => (
          <button key={value} type="button" onClick={() => setStatusFilter(value)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${statusFilter === value ? 'bg-emerald-600 text-white' : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}>
            {label}
          </button>
        ))}
        {roleOptions.map((role) => (
          <button
            key={role.code}
            type="button"
            onClick={() => {
              setRoleFilter(role.code);
              if (!editingUserId && showCreateForm) {
                setForm((current) => ({
                  ...current,
                  role: role.code === 'all' ? current.role : role.code,
                  class_id: role.code === 'student' || role.code === 'all' ? current.class_id : '',
                  linked_student_ids: role.code === 'parent' || role.code === 'all' ? current.linked_student_ids : [],
                }));
              }
            }}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              roleFilter === role.code
                ? 'bg-blue-600 text-white'
                : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {role.name}
          </button>
        ))}
        <select
          value={employmentTypeFilter}
          onChange={(event) => setEmploymentTypeFilter(event.target.value)}
          className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600"
        >
          <option value="">All employment types</option>
          {employmentTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <select
          value={workArrangementFilter}
          onChange={(event) => setWorkArrangementFilter(event.target.value)}
          className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600"
        >
          <option value="">All work modes</option>
          {workArrangementOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </div>

      {showCreateForm && (canCreate || canEdit) && (
        <form
          onSubmit={handleCreateUser}
          className="grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm md:grid-cols-2 xl:grid-cols-3"
        >
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Full Name</label>
            <input
              type="text"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ravi Kumar"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="ravi@example.com"
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm({
                ...form,
                role: e.target.value,
                attendance_enabled: ['student', 'teacher', 'employee', 'manager'].includes(e.target.value),
                class_id: e.target.value === 'student' ? form.class_id : '',
                linked_student_ids: e.target.value === 'parent' ? form.linked_student_ids : [],
              })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {roles.map((role) => (
                <option key={role.id} value={role.code}>{getRoleDisplayName(role.code, entityCategory, role.name)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Department</label>
            <select
              value={form.department_id}
              onChange={(e) => setForm({ ...form, department_id: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select department</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
          </div>

          {(form.role === 'student' || (entityCategory === 'office' && form.attendance_enabled)) && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">{terminology.group}</label>
              <select
                value={form.class_id}
                onChange={(e) => setForm({ ...form, class_id: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select {terminology.group.toLowerCase()}</option>
                {sections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {form.role === 'parent' && (
            <div className="md:col-span-2 xl:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">Linked {terminology.people}</label>
              <select
                multiple
                value={form.linked_student_ids.map(String)}
                onChange={(e) => setForm({
                  ...form,
                  linked_student_ids: Array.from(e.target.selectedOptions).map((option) => Number(option.value)),
                })}
                className="min-h-[132px] w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {studentOptions.map((student) => (
                  <option key={student.id} value={student.id}>
                    {student.full_name} {student.employee_id ? `(${student.employee_id})` : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">Hold Ctrl or Cmd to select more than one {terminology.person.toLowerCase()}.</p>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {form.role === 'student'
                ? terminology.personId
                : `${getRoleDisplayName(form.role, entityCategory)} ID`}
            </label>
            <input
              type="text"
              value={form.employee_id}
              onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={form.role === 'teacher' ? 'SUP001' : form.role === 'parent' ? 'VIEW001' : 'EMP001'}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Phone</label>
            <input
              type="text"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="+91 9876543210"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Employment Type</label>
            <select
              value={form.employment_type}
              onChange={(e) => setForm({ ...form, employment_type: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Not set</option>
              {employmentTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Work Arrangement</label>
            <select
              value={form.work_arrangement}
              onChange={(e) => setForm({ ...form, work_arrangement: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Not set</option>
              {workArrangementOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2 xl:col-span-3">
            <label className="mb-1 block text-sm font-medium text-gray-700">Password (optional)</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Set only if this person needs to log in"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {editingUserId ? 'Status' : 'Initial Status'}
            </label>
            <select
              value={form.is_active === false ? 'inactive' : 'active'}
              onChange={(e) => setForm({ ...form, is_active: e.target.value === 'active' })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="inactive">Inactive — directory only</option>
              <option value="active">Active — track attendance immediately</option>
            </select>
          </div>

          <label className="flex items-start gap-3 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.attendance_enabled}
              onChange={(e) => setForm({
                ...form,
                attendance_enabled: e.target.checked,
                class_id: e.target.checked ? form.class_id : '',
              })}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600"
            />
            <span>
              <span className="block font-medium">Track this user&apos;s attendance</span>
              <span className="mt-0.5 block text-xs text-gray-500">
                Enable this for employees who also have an Administrator or other access role.
              </span>
            </span>
          </label>

          <div className="flex gap-3 md:col-span-2 xl:col-span-3">
            <button
              type="submit"
              disabled={savingUser}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {savingUser ? 'Saving...' : editingUserId ? 'Update User' : 'Create User'}
            </button>
            <button
              type="button"
              onClick={() => {
                setForm(emptyForm);
                setEditingUserId(null);
                setShowCreateForm(false);
              }}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-100 p-4 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <Search size={16} className="absolute left-3 top-3 text-gray-400" />
            <input type="text" placeholder="Search by name, email or ID..." value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-xs text-gray-500">Move table</span>
            <button type="button" onClick={() => scrollUsersTable(-1)} aria-label="Scroll user table left"
              className="rounded-lg border border-gray-200 bg-white p-2 text-gray-600 hover:bg-gray-50">
              <ChevronLeft size={16} />
            </button>
            <button type="button" onClick={() => scrollUsersTable(1)} aria-label="Scroll user table right"
              className="rounded-lg border border-gray-200 bg-white p-2 text-gray-600 hover:bg-gray-50">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        <div ref={tableScrollRef} className="max-w-full overflow-x-auto overscroll-x-contain">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading...</div>
          ) : (
            <table className="min-w-[1680px] w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Role</th>
                  <th className="text-left px-4 py-3">Department</th>
                  <th className="text-left px-4 py-3">Employment</th>
                  <th className="text-left px-4 py-3">Work Mode</th>
                  <th className="text-left px-4 py-3">{roleFilter === 'student' ? terminology.group : roleFilter === 'parent' ? `Linked ${terminology.people}` : 'Assignment'}</th>
                  <th className="text-left px-4 py-3">
                    {roleFilter === 'student' ? terminology.personId : roleFilter === 'all' ? 'User ID' : `${getRoleDisplayName(roleFilter, entityCategory)} ID`}
                  </th>
                  <th className="text-left px-4 py-3">RFID Tag</th>
                  <th className="text-left px-4 py-3">Mobile App</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((user) => (
                  <tr key={user.id} className={`hover:bg-gray-50 ${user.lifecycle_status === 'deactivated' ? 'bg-gray-50 opacity-75' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-100 text-blue-700 rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                          {user.full_name?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{user.full_name}</p>
                          <p className="text-xs text-gray-400">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                        {getRoleDisplayName(user.role, entityCategory)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{user.department || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{getEmploymentTypeLabel(user.employment_type)}</td>
                    <td className="px-4 py-3 text-gray-500">{getWorkArrangementLabel(user.work_arrangement)}</td>
                    <td className="px-4 py-3">
                      {(user.role === 'student' || (entityCategory === 'office' && user.attendance_enabled)) ? (
                        <select
                          value={user.section_id || ''}
                          onChange={(e) => handleSectionChange(user.id, e.target.value)}
                          disabled={!canEdit || user.lifecycle_status === 'deactivated'}
                          className="rounded-lg border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">No {terminology.group.toLowerCase()}</option>
                          {sections.map((section) => (
                            <option key={section.id} value={section.id}>
                              {section.name}
                            </option>
                          ))}
                        </select>
                      ) : user.role === 'parent' ? (
                        Array.isArray(user.linked_students) && user.linked_students.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {user.linked_students.map((student) => (
                              <span key={student.id} className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700">
                                {student.full_name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">No linked {terminology.people.toLowerCase()}</span>
                        )
                      ) : (
                        <span className="text-gray-400 text-xs">
                          {getRoleDisplayName(user.role, entityCategory)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{user.employee_id || '—'}</td>
                    <td className="px-4 py-3">
                      {user.rfid_tag ? (
                        <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{user.rfid_tag}</span>
                      ) : (
                        <span className="text-red-400 text-xs">No tag</span>
                      )}
                    </td>
                    <td className="px-4 py-3 min-w-[220px]">
                      <div className="flex items-start gap-2">
                        <Smartphone size={16} className={user.mobile_device_id ? 'mt-0.5 text-emerald-600' : 'mt-0.5 text-gray-400'} />
                        <div>
                          <p className={`text-xs font-medium ${user.mobile_device_id ? 'text-emerald-700' : 'text-gray-500'}`}>
                            {user.mobile_device_id ? 'Phone registered' : 'No phone registered'}
                          </p>
                          <p className="mt-0.5 text-[10px] text-gray-500">
                            Login {user.login_enabled ? 'enabled' : 'not configured'} · Face {user.face_enrolled_at ? 'enrolled' : 'not enrolled'}
                          </p>
                          {user.mobile_device_id && <>
                            <p className="mt-0.5 max-w-[190px] truncate text-[10px] text-gray-500" title={user.mobile_device_name || ''}>
                              {user.mobile_device_name || user.mobile_device_platform || 'Android device'}
                            </p>
                            <p className="text-[10px] text-gray-400">
                              Last seen {user.mobile_device_last_seen_at ? new Date(user.mobile_device_last_seen_at).toLocaleString() : 'never'}
                            </p>
                            {canManageMobileDevice && user.lifecycle_status !== 'deactivated' && (
                              <button type="button" onClick={() => handleReplaceMobileDevice(user)}
                                className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-800">
                                <RotateCcw size={11} /> Replace device
                              </button>
                            )}
                          </>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${user.lifecycle_status === 'deactivated' ? 'bg-gray-200 text-gray-700' : user.is_active ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {user.lifecycle_status === 'deactivated' ? 'Deactivated' : user.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <p className="mt-1 whitespace-nowrap text-[10px] text-gray-400">
                        {!user.attendance_enabled
                          ? 'Attendance not enabled'
                          : user.lifecycle_status === 'deactivated' && user.archived_at
                          ? `Archived ${new Date(user.archived_at).toLocaleString()}`
                          : user.is_active && user.activated_at
                          ? `Tracking since ${new Date(user.activated_at).toLocaleString()}`
                          : user.deactivated_at
                            ? `Stopped ${new Date(user.deactivated_at).toLocaleString()}`
                            : 'Attendance not started'}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      {user.lifecycle_status !== 'deactivated' && canAssignTag && assigning === user.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            placeholder="Tag UID"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value.toUpperCase())}
                            className="border border-gray-300 rounded px-2 py-1 text-xs w-36 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <button
                            onClick={() => handleAssignTag(user.id)}
                            className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => {
                              setAssigning(null);
                              setTagInput('');
                            }}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : user.lifecycle_status !== 'deactivated' && canAssignTag ? (
                        <button
                          onClick={() => {
                            setAssigning(user.id);
                            setTagInput(user.rfid_tag || '');
                          }}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                        >
                          <Tag size={12} />
                          {user.rfid_tag ? 'Change Tag' : 'Assign Tag'}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">No tag permission</span>
                      )}
                      {canEdit && user.lifecycle_status === 'deactivated' ? (
                        <button onClick={() => handleRestore(user)}
                          className="mt-2 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                          <Power size={12} /> Restore to Inactive
                        </button>
                      ) : canEdit && (
                        <>
                          <button
                            onClick={() => startEditUser(user)}
                            className="mt-2 flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800"
                          >
                            <Pencil size={12} />
                            Edit User
                          </button>
                          <button onClick={() => handleStatusChange(user, !user.is_active)}
                            className={`mt-2 flex items-center gap-1 text-xs ${user.is_active ? 'text-red-600 hover:text-red-800' : 'text-emerald-600 hover:text-emerald-800'}`}>
                            <Power size={12} />
                            {user.is_active ? 'Move to Inactive' : 'Activate Attendance'}
                          </button>
                          <button onClick={() => handleDeactivate(user)}
                            className="mt-2 flex items-center gap-1 text-xs text-gray-600 hover:text-red-700">
                            <Power size={12} /> Deactivate
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;
